import { useAppbarTitle } from '@hooks/useAppbarTitle';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { usePageTitle } from '@hooks/usePageTitle';
import { useWindowSize } from '@hooks/useWindowSize';
import { mdiArrowLeft } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Button, Skeleton } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetOffersSpecialist, useMonitoringOffersStore } from 'services/monitoring-offers';
import { chatService } from '../../../services/chat/chatService';
import MonitoringCardSpecialist from '../components/Card/MonitoringCardSpecialist';
import { Empty } from '../components/Empty';

const styles = {
	container: {
		display: 'flex',
		flexDirection: 'column',
		boxShadow: 3,
		position: 'relative',
		bgcolor: 'white',
		borderRadius: 3,
	},
};

export const MonitoringOfferPageSpecialist = () => {
	const { navigateWithLanguage } = useLanguageNavigate();
	const { t } = useTranslation();
	const size = useWindowSize();
	// Track loading state per monitoring ID
	const [loadingChats, setLoadingChats] = useState<Record<string, boolean>>({});

	usePageTitle(t('offers'));
	useAppbarTitle('offers');

	const { isLoading } = useGetOffersSpecialist();
	const { ongoingOffers } = useMonitoringOffersStore();

	// Handle chat button click with on-demand channel fetching
	const handleChat = useCallback(
		async (monitoringId: string) => {
			// Set loading state for this specific monitoring ID
			setLoadingChats(prev => ({ ...prev, [monitoringId]: true }));
			
			try {
				const channel = await chatService.GetChannelByMonitoringId(monitoringId);
				if (channel?.id) {
					navigateWithLanguage(`/specialist/chats?channelId=${channel.id}`);
				} else {
					console.error('No channel ID found for monitoring offer');
				}
			} catch (error) {
				console.error('Error fetching channel:', error);
			} finally {
				// Clear loading state for this specific monitoring ID
				setLoadingChats(prev => ({ ...prev, [monitoringId]: false }));
			}
		},
		[navigateWithLanguage]
	);

	// Function to check if a specific chat is loading
	const isChatLoading = useCallback((monitoringId: string) => {
		return !!loadingChats[monitoringId];
	}, [loadingChats]);

	const handleSelectSpecialists = async (data: any) => {
		localStorage.setItem('selectedOffer', JSON.stringify(data));
		navigateWithLanguage(`/specialist/monitoring/project/${data?.id}`);
	};

	return (
		<Box sx={{ ...styles.container, minHeight: size.width > 768 ? '100%' : 'auto' }}>
			<div className="flex justify-center items-center">
				<div className="absolute left-[20px] top-[50px] md:top-[20px]">
					<Button
						className="w-[120px]"
						onClick={() => navigateWithLanguage('/specialist/view-offers')}
						variant="text"
						sx={{ backgroundColor: 'white', color: 'black' }}
						startIcon={<Icon path={mdiArrowLeft} size={1} />}
					>
						{t('previous')}
					</Button>
				</div>
				<h2 className="md:md:text-2xl font-bold mb-6 mt-5">
					{t('requestsAndOffers.viewRequestsAndOffers')}
				</h2>
			</div>
			<div className="p-[20px]">
				{isLoading ? (
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
						{Array.from({ length: 6 }).map((_, index) => (
							<Box key={index} sx={{ padding: 2, boxShadow: 3, borderRadius: 3, bgcolor: 'white' }}>
								<Skeleton variant="rectangular" height={150} />
								<Skeleton variant="text" sx={{ marginTop: 1, height: 30 }} />
								<Skeleton variant="text" width="80%" />
							</Box>
						))}
					</div>
				) : ongoingOffers?.length === 0 ? (
					<Empty
						title={t('requestsAndOffers.noRequestsAndOffersYetSpecialist')}
						buttonTitle={t('requestsAndOffers.ongoingProjects')}
						route="/specialist/monitoring/ongoing-projects"
					/>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
						{ongoingOffers?.map((data: any, index: number) =>
							data?.id ? (
								<MonitoringCardSpecialist
									key={data.id}
									data={data}
									loading={false}
									onSelectSpecialists={() => handleSelectSpecialists(data)}
									onChat={handleChat}
									chatLoading={isChatLoading(data?.id)}
								/>
							) : (
								<div key={index}>Loading...</div>
							)
						)}
					</div>
				)}
			</div>
		</Box>
	);
};
